import React, { useEffect } from 'react';
import { X, MapPin, DollarSign, Heart, ChevronRight, Briefcase } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { overlayDismiss } from '../utils/overlayDismiss';
import { jobTitle, groupSkills } from '../constants/jobOptions';
import './JobDetailSheet.css';

export default function JobDetailSheet({ job, onClose, onApply }) {
  const { user, openAuth, appliedJobs } = useApp();
  const hasApplied = appliedJobs.some(j => j.id === job.id);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleApply = () => {
    if (!user) {
      openAuth('register');
      return;
    }
    onApply();
  };

  return (
    <div className="detail-sheet-overlay" {...overlayDismiss(onClose)}>
      <div className="detail-sheet animate-slideInUp">
        {/* Handle bar */}
        <div className="detail-sheet__handle" />

        {/* Header */}
        <div className="detail-sheet__header">
          <button className="detail-sheet__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="detail-sheet__body">
          {/* Company hero */}
          <div className="detail-sheet__company-row">
            <div className="detail-sheet__logo">
              {job.logo_url
                ? <img src={job.logo_url} alt={job.company} />
                : <span>{(job.company || 'J').slice(0, 2).toUpperCase()}</span>
              }
            </div>
            <div>
              <h2 className="detail-sheet__title">{jobTitle(job.title)}</h2>
              <p className="detail-sheet__company">{job.company}</p>
            </div>
          </div>

          {/* Meta chips */}
          <div className="detail-sheet__chips">
            {job.location && (
              <div className="detail-sheet__chip">
                <MapPin size={14} /> {job.location}
              </div>
            )}
            {job.type && (
              <div className="detail-sheet__chip">
                <Briefcase size={14} /> {job.type}
              </div>
            )}
            {(job.salary_display || job.salary) && (
              <div className="detail-sheet__chip detail-sheet__chip--green">
                <DollarSign size={14} /> {job.salary_display || job.salary}
              </div>
            )}
          </div>

          {/* Tags */}
          {Array.isArray(job.tags) && job.tags.length > 0 && (
            <div className="detail-sheet__tags">
              {job.tags.map(tag => (
                <span key={tag} className="detail-sheet__tag">{tag}</span>
              ))}
            </div>
          )}

          {/* Description */}
          {job.description && (
            <div className="detail-sheet__section">
              <h3>Jobbeschreibung</h3>
              <p>{job.description}</p>
            </div>
          )}

          {/* Requirements */}
          {job.requirements && (
            <div className="detail-sheet__section">
              <h3>Anforderungen</h3>
              <p>{job.requirements}</p>
            </div>
          )}

          {job.skills?.length > 0 && (
            <div className="detail-sheet__section">
              <h3>Skills</h3>
              {groupSkills(job.skills).map(group => (
                <div key={group.title}>
                  <h4 className="detail-sheet__skill-title">{group.title}</h4>
                  <div className="detail-sheet__tags">
                    {group.skills.map(s => <span key={s} className="detail-sheet__tag">{s}</span>)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Working hours */}
          {job.hours && (
            <div className="detail-sheet__section">
              <h3>Arbeitszeiten</h3>
              <p>{job.hours}</p>
            </div>
          )}

          {/* Fallback teaser */}
          {!job.description && (
            <div className="detail-sheet__section">
              <p style={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>
                Klicke auf "Jetzt bewerben" – der Arbeitgeber meldet sich dann bei dir!
              </p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="detail-sheet__footer">
          {hasApplied ? (
            <div className="detail-sheet__applied">
              <Heart size={18} fill="currentColor" /> Interesse gesendet!
            </div>
          ) : (
            <button className="detail-sheet__apply-btn" onClick={handleApply}>
              <Heart size={18} fill="white" />
              Jetzt bewerben
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
